import * as React from 'react'
import * as PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Slide } from 'react-reveal'
import Barcode from 'react-barcode'
import QRCode from 'qrcode.react'

import Icon from '../../../../components/ui/Icon/Icon'

import MoreHeaderParticles from '../../../../assets/img/more-header-particles.svg'
import AppStore from '../../../../assets/img/app-download/app-store.png'
import PlayStore from '../../../../assets/img/app-download/play-store.png'

import downloadAppLink from '../../../../services/redirection/downloadAppLink'
import dateFormat, {
  timeFormat,
  dateTimeFormat,
} from '../../../../services/data/dateFormat'

import watchMovieImage from '../../../../assets/img/watchMovie.svg'

const ReturnIcon = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  cursor: pointer;
  font-size: 0.92rem;
  margin-left: 20px !important;
  margin-right: 20px !important;
  color: var(--color-fg-light);
  background: rgba(255, 255, 255, 0.5);
  z-index: 1;

  & > svg {
    position: relative;
    color: #ffffff;
  }
`

const Title = styled.h1`
  flex: 1;
  padding: 20px 0;
  margin: 0;
  margin-right: 60px;
  color: #ffffff;
  font-size: 1.125rem;
  font-weight: var(--font-weight-boldest);
  text-transform: uppercase;
  text-align: center;
`

const MainContent = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  margin-top: -3.125rem;
`

const MainHeader = styled.div`
  position: relative;
  padding: 2rem 1.0625rem 1.1875rem;
  border-bottom: 1.5px dashed var(--color-fg-lighter);

  &::before,
  &::after {
    content: ' ';
    bottom: -0.7rem;
    position: absolute;
    height: 20px;
    width: 10px;
    border: 1px solid #e5e5e5;
    background: #ffffff;
  }

  &::before {
    left: -1px;
    border-radius: 0 16px 16px 0;
    border-left-color: #ffffff;
  }

  &::after {
    right: -1px;
    border-radius: 16px 0 0 16px;
    border-right-color: #ffffff;
  }
`

const MovieTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.5px;
  text-align: center;
  margin: 0;
`

const Theater = styled.p`
  color: #9b9b9b;
  font-size: 0.8rem;
  letter-spacing: 0.5;
  text-align: center;
  margin: 0;
  margin-bottom: 1.375rem;
`

const TicketDetails = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5625rem;
`

const Date = styled.div`
  background: #eb7b88;
  flex: 0 1 100px;
  text-align: center;
  border-radius: 0.3rem;
  font-size: 0.54rem;
  line-height: 1rem;
  padding: 0.2rem 0.5rem;
  color: #fff;
`

const MainDetails = styled.div`
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: column;
`

const TicketDetailsSection = styled.div`
  font-size: 0.8rem;
  line-height: 0.9rem;
`

const DetailPageContainer = styled.div`
  display: ${props => (props.show ? 'block' : 'none')};
  background-color: inherit;
  transition-property: width;
  transition-duration: var(--animation-duration);
  transition-timing-function: var(--animation-linear);
  overflow: auto;
  height: 100vh;
`

const DetailPageBackground = styled.div.attrs(props => ({
  style: {
    background: `url('${props.background}') center / cover`,
  },
}))`
  display: ${props => (props.show ? 'block' : 'none')};
  height: 232px;
  background-color: var(--color-primary);
`

const DetailHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  min-height: 87px;
  font-size: 0.7rem;
  z-index: 100;

  ${props =>
    props.hasScrolled &&
    css`
    background: url('${MoreHeaderParticles}'), linear-gradient(var(--color-primary), var(--color-primary));
    background-repeat: no-repeat;
    background-position: right top;
  `}
`

const DetailPageContent = styled.div`
  padding: 1.5625rem;
`

const SeatsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const Seat = styled.div`
  color: var(--color-primary);
  text-align: center;
  font-size: 0.625rem;
  background: #eaf1f9;
  border-radius: 4px;

  padding: 0.5rem 1rem;
  flex: 0 0 20%;
  margin: 0.5rem 2.5%;
`

const CodeList = styled.div`
  margin-top: 2rem;
  width: 100%;
`

const CodeWrapper = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`

const BarcodeWrapper = styled.div`
  text-align: center;

  svg {
    max-width: 100%;
  }
`

const Code = styled.p`
  color: #9b9b9b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0;
`

const StyledQRCode = styled(QRCode)`
  width: 100% !important;
  height: 100% !important;
  padding: 0 2rem;
  box-sizing: border-box;
`

const SectionTitle = styled.p`
  color: var(--color-fg-light);
  font-size: 0.7rem;
  line-height: 0.7rem;
  margin: 0;
`

const ClaimedWrapper = styled.div`
  display: flex;
`

const ClaimedDetails = styled.div`
  flex: 1 1 auto;
  margin-right: 1rem;
`

const ClaimedTitle = styled.h3`
  color: var(--color-primary);
  font-size: 1.25rem;
  font-weight: var(--font-weight-boldest);
  letter-spacing: 0.42px;
  margin: 0;
`

const ClaimedDate = styled.p`
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.5px;
  margin: 0;
  margin-bottom: 1rem;
`

const ClaimedMessage = styled.p`
  color: #9b9b9b;
  font-size: 0.75rem;
  line-height: 1rem;
  letter-spacing: 0.43px;
  margin: 0;
`

const ClaimedImage = styled.img.attrs({ src: watchMovieImage })`
  flex: 0 0 auto;
  margin-bottom: 2rem;
`

const UseZingAppCaption = styled.div`
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: var(--font-weight-boldest);
  text-align: center;
  background: var(--color-primary);
  border-radius: var(--border-radius-card);
  padding: 0.625rem 1rem;
`

const UseZingAppDetails = styled.p`
  font-size: 0.875rem;
  text-align: center;
`

const StoreBubbles = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 1.5rem;
`

const Store = styled.img`
  cursor: pointer;
  height: 39px;
  margin: 0.5rem;
`

class TicketsPageDetails extends React.Component {
  state = {
    detailHasScrolled: false,
  }

  detailPage = React.createRef()

  componentDidMount() {
    this.detailPage.current.addEventListener('scroll', this.handleScrollDetails)
  }

  componentWillUnmount() {
    this.detailPage.current.removeEventListener(
      'scroll',
      this.handleScrollDetails,
    )
  }

  handleScrollDetails = event => {
    const { detailHasScrolled: detailHasScrolledState } = this.state
    const { scrollTop } = event.target
    const detailHasScrolled = scrollTop > 0

    if (detailHasScrolled !== detailHasScrolledState) {
      this.setState({
        detailHasScrolled,
      })
    }
  }

  openTab = link => {
    window.open(link)
  }

  render() {
    const { detailHasScrolled } = this.state
    const { data, onCloseDetailPage } = this.props

    const show = !!data
    const {
      movie = {},
      seats,
      theater,
      reserveAt,
      cinema,
      status,
      claimMessage,
      qrType,
      qrCode,
    } = data || {}
    const { title, bannerImageUrl } = movie

    return (
      <Slide right duration={200}>
        <DetailPageContainer ref={this.detailPage} show={show}>
          <DetailPageBackground show={show} background={bannerImageUrl} />
          <DetailHeader hasScrolled={detailHasScrolled}>
            <ReturnIcon onClick={onCloseDetailPage}>
              <Icon name="slide-left" />
            </ReturnIcon>
            <Title>My Movie Tickets</Title>
          </DetailHeader>
          {!!show && (
            <>
              <DetailPageContent>
                <MainContent>
                  <MainHeader>
                    <MovieTitle>{title}</MovieTitle>
                    <Theater>@ {theater}</Theater>
                    <TicketDetails>
                      <Date>{dateFormat(reserveAt)}</Date>
                      <TicketDetailsSection>
                        <SectionTitle>Time</SectionTitle>
                        {timeFormat(reserveAt)}
                      </TicketDetailsSection>
                      <TicketDetailsSection>
                        <SectionTitle>Theater</SectionTitle>
                        {cinema}
                      </TicketDetailsSection>
                      <TicketDetailsSection>
                        <SectionTitle>Seats</SectionTitle>
                        {seats.length} seats
                      </TicketDetailsSection>
                    </TicketDetails>
                    <SeatsWrapper>
                      {seats.map((seat, index) => (
                        <Seat key={seat}>{seat}</Seat>
                      ))}
                    </SeatsWrapper>
                  </MainHeader>
                  <MainDetails>
                    <CodeList>
                      {qrCode.map(code => (
                        <CodeWrapper>
                          {qrType === 'barcode' ? (
                            <BarcodeWrapper>
                              <Barcode
                                height="23px"
                                displayValue={false}
                                value={code || ''}
                                background="transparent"
                              />
                              <Code>{code}</Code>
                            </BarcodeWrapper>
                          ) : (
                            <StyledQRCode value={code || ''} />
                          )}
                        </CodeWrapper>
                      ))}
                    </CodeList>
                    {status === 1 ? (
                      <ClaimedWrapper>
                        <ClaimedDetails>
                          <ClaimedTitle>Claimed!</ClaimedTitle>
                          <ClaimedDate>{dateTimeFormat(reserveAt)}</ClaimedDate>
                          <ClaimedMessage>{claimMessage}</ClaimedMessage>
                        </ClaimedDetails>
                        <ClaimedImage />
                      </ClaimedWrapper>
                    ) : status === 2 ? (
                      <></>
                    ) : (
                      <>
                        <UseZingAppCaption>
                          Use the Zing app to claim your ticket
                        </UseZingAppCaption>
                        <UseZingAppDetails>
                          Don't have a Zing app yet? <br /> Download here
                        </UseZingAppDetails>
                        <StoreBubbles>
                          <Store
                            src={AppStore}
                            onClick={downloadAppLink('iOS')}
                            alt=""
                          />
                          <Store
                            src={PlayStore}
                            onClick={downloadAppLink('Android')}
                            alt=""
                          />
                        </StoreBubbles>
                      </>
                    )}
                  </MainDetails>
                </MainContent>
              </DetailPageContent>
            </>
          )}
        </DetailPageContainer>
      </Slide>
    )
  }
}

TicketsPageDetails.defaultProps = {
  messages: [],
}

TicketsPageDetails.propTypes = {
  messages: PropTypes.array,
}

export default TicketsPageDetails
